import { useEffect, useMemo } from 'react';
import { api, type OnlinePerson } from '../../api/client';
import { Card, Chip, Empty, LoadError, Loading, PageHeader } from '../../components/ui';
import { articleFor } from '../../help';
import { useAuth } from '../../lib/auth';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

const minutes = (since: string, now: number) => {
  const m = Math.max(0, Math.round((now - new Date(since).getTime()) / 60_000));
  return m < 1 ? 'just now' : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

/** The page someone is on, in the words of the help center ("Ring groups"), or its address. */
export const pageName = (path: string) => (path.startsWith('/help') ? 'Help center' : (articleFor(path)?.title ?? path));

/** Who is using Stitch right now, and joining their screen to help. */
export function AdminLive() {
  const { state } = useAuth();
  const list = useData(() => api.liveOnline(), []);
  const reload = list.reload;
  useEffect(() => {
    const t = window.setInterval(reload, 5000);
    return () => window.clearInterval(t);
  }, [reload]);

  // One row a person: their most recent tab, with a count of the others.
  const people = useMemo(() => {
    const by = new Map<string, { p: OnlinePerson; tabs: number }>();
    for (const p of list.data?.people ?? []) {
      const had = by.get(p.id);
      if (had) had.tabs++;
      else by.set(p.id, { p, tabs: 1 });
    }
    return [...by.values()];
  }, [list.data]);
  const now = Date.now();
  const me = state?.user?.name;

  return (
    <>
      <PageHeader eyebrow="Platform" title="Live help" chips={people.length ? <Chip tone="ok">{people.length} online</Chip> : undefined} />
      <div className="content">
        <p className="page-lede">
          Everyone using Stitch right now. Join someone’s screen to see what they see and point things out; with their go-ahead, click and type for them. They’re asked first, nothing is
          recorded, and they can stop at any time.
        </p>
        {list.error ? (
          <Card>
            <LoadError error={list.error} retry={list.reload} />
          </Card>
        ) : !list.data ? (
          <Card>
            <Loading label="Loading who’s online" />
          </Card>
        ) : people.length === 0 ? (
          <Card>
            <Empty title="Nobody is online right now">People appear here a few seconds after they open Stitch, and drop off a minute after they close it.</Empty>
          </Card>
        ) : (
          <Card>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Company</th>
                    <th>Where</th>
                    <th>Device</th>
                    <th>Here for</th>
                    <th className="right">
                      <span className="sr-only">Join</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {people.map(({ p, tabs }) => (
                    <tr key={p.id}>
                      <td>
                        <span className="cell-stack">
                          <strong>{p.name || p.email}</strong>
                          <span className="muted">{p.email}</span>
                        </span>
                      </td>
                      <td>
                        <span className="cell-stack">
                          {p.company ?? <span className="muted">None</span>}
                          {p.role && <span className="muted">{p.role === 'admin' ? 'Admin' : 'Their own phone'}</span>}
                        </span>
                      </td>
                      <td>
                        <span className="cell-stack">
                          <span title={p.page}>{pageName(p.page)}</span>
                          {(!p.visible || tabs > 1) && (
                            <span className="muted">
                              {[!p.visible && 'In the background', tabs > 1 && `${tabs} tabs open`].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </span>
                      </td>
                      <td>{p.device}</td>
                      <td>{minutes(p.since, now)}</td>
                      <td className="right">
                        {p.session && p.session.staffName !== me ? (
                          <Chip tone="warn">{p.session.staffName} is with them</Chip>
                        ) : p.session ? (
                          <a className="btn secondary small" href={`/live/${encodeURIComponent(p.session.id)}`} target="_blank" rel="noopener">
                            <Icon name="screen" size={15} /> Open your session
                          </a>
                        ) : (
                          <a className="btn primary small" href={`/live/new?user=${encodeURIComponent(p.id)}`} target="_blank" rel="noopener" aria-label={`Join ${p.name || p.email}’s screen`}>
                            <Icon name="screen" size={15} /> Join their screen
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
